import { Button } from '@/components/shadcn/button'
import {
  ChartContainer,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  getChartLegendItems,
} from '@/components/shadcn/charts'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { getContrastTextColor } from '@/lib/utils'
import { IconRefreshOutline } from '@icons/IconRefreshOutline'
import clsx from 'clsx'
import { Bar, BarChart, CartesianGrid, Cell, Customized, XAxis, YAxis } from 'recharts'
import { ComponentActionsContainer } from '../shared/ComponentActionsContainer'
import { ComponentHeader } from '../shared/ComponentHeader'
import { ComponentTitle } from '../shared/ComponentTitle'
import {
  calculateAngledAxisBandSize,
  calculateCategoryYAxisWidth,
  calculateVerticalTickAxisBandSize,
  CATEGORY_LABEL_WRAP_WIDTH,
  TICK_LINE_HEIGHT,
  wrapCategoryLabelLines,
} from '../shared/chartAxisUtils'
import { normalizeRechartsData, remapRechartsRecordKeys } from '../shared/rechartsDataUtils'
import { getComponentHeights } from '../utils'
import { ChartBarDataSchema, ChartBarMarkerConfig, ChartBarUISchema, DataItem } from './types'
import { ChartBarEmptyResponse } from './ChartBarEmptyResponse'
import { computeBarChartDomain, generateDataAxisTicks, hasChartBarData } from './chartBarUtils'

const CAN_REFRESH = true

const BAR_LABEL_OFFSET = 8
const BAR_LABEL_CHAR_WIDTH = 7
const BAR_LABEL_MIN_PADDING = 4

interface BarLabelContentProps {
  x?: number
  y?: number
  width?: number
  height?: number
  value?: number | [number, number]
  index?: number
}

function renderBarLabel(
  props: BarLabelContentProps,
  options: { isVertical: boolean; segmentValue: number; fill: string }
) {
  const { x = 0, y = 0, width = 0, height = 0 } = props
  const { isVertical, segmentValue, fill } = options

  if (segmentValue === 0) return null

  const labelText = `${segmentValue}`
  const segmentSize = isVertical ? width : height
  const minRequiredSize = labelText.length * BAR_LABEL_CHAR_WIDTH + BAR_LABEL_OFFSET + BAR_LABEL_MIN_PADDING

  if (segmentSize < minRequiredSize) return null

  const labelX = isVertical ? x + width - BAR_LABEL_OFFSET : x + width / 2
  const labelY = isVertical ? y + height / 2 : y + BAR_LABEL_OFFSET

  return (
    <text
      x={labelX}
      y={labelY}
      fill={fill}
      fillOpacity={0.8}
      textAnchor={isVertical ? 'end' : 'middle'}
      dominantBaseline={isVertical ? 'central' : 'hanging'}
    >
      {labelText}
    </text>
  )
}

function getStackedBarSegmentValue(
  entry: { value?: number | [number, number]; payload?: DataItem },
  dataKey: string
): number {
  if (Array.isArray(entry.value)) {
    return entry.value[1] - entry.value[0]
  }

  if (entry.payload) {
    const rawValue = Number(entry.payload[dataKey as keyof DataItem])
    if (!Number.isNaN(rawValue)) return rawValue
  }

  return 0
}

// Custom marker renderer component for drawing target/goal lines on bars
interface BarMarkersProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  xAxisMap?: Record<string, any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  yAxisMap?: Record<string, any>
  data: DataItem[]
  markers: ChartBarMarkerConfig[]
  categoryKey: string
  allBarDataKeys: string[]
  isVertical: boolean
  stacked?: boolean
}

function BarMarkers({
  xAxisMap,
  yAxisMap,
  data,
  markers,
  categoryKey,
  allBarDataKeys,
  isVertical,
  stacked,
}: BarMarkersProps) {
  if (!xAxisMap || !yAxisMap) return null

  const xAxis = Object.values(xAxisMap)[0]
  const yAxis = Object.values(yAxisMap)[0]

  if (!xAxis || !yAxis) return null

  // Get the scale for the category and value axes
  const categoryAxis = isVertical ? yAxis : xAxis
  const valueAxis = isVertical ? xAxis : yAxis

  // Get the full bandwidth allocated to each category
  const bandwidth = categoryAxis.bandSize || categoryAxis.bandwidth || 20

  // Recharts applies a default barCategoryGap (10% on each side by default)
  // This means the actual bar area is smaller than the bandwidth
  const barCategoryGapPercent = 0.1 // 10% gap on each side
  const actualBarAreaWidth = bandwidth * (1 - 2 * barCategoryGapPercent)
  const barAreaOffset = bandwidth * barCategoryGapPercent

  const barCount = allBarDataKeys.length
  // When stacked, all bars occupy the full width; when not stacked, divide by bar count
  const barWidth = stacked ? actualBarAreaWidth : actualBarAreaWidth / barCount

  // Inset percentage to stay within the visible bar area
  const insetPercent = 0.1

  return (
    <g className="bar-markers">
      {markers.map((marker, markerIndex) => {
        const { dataKey, color = '#1f2937', strokeWidth = 2, barDataKeys } = marker

        // Determine which bars this marker should be drawn on
        // When stacked, barDataKeys filtering still applies but position is the same
        const targetBarKeys = barDataKeys && barDataKeys.length > 0 ? barDataKeys : allBarDataKeys

        // For stacked bars, we only need to draw one marker per category (not per bar)
        // since all bars in a stack occupy the same space
        const keysToRender = stacked ? [targetBarKeys[0]] : targetBarKeys

        return data.map((item, dataIndex) => {
          const markerValue = Number(item[dataKey])
          if (isNaN(markerValue)) return null

          const categoryValue = item[categoryKey]
          const categoryPos = categoryAxis.scale(categoryValue)

          if (categoryPos === undefined) return null

          // Calculate the marker position on the value axis
          const markerPos = valueAxis.scale(markerValue)

          // The scale returns the start of the band
          // Add the bar area offset to account for barCategoryGap
          const barAreaStart = categoryPos + barAreaOffset

          // Draw a marker line for each targeted bar
          return keysToRender.map((barKey) => {
            const barIndex = allBarDataKeys.indexOf(barKey)
            if (barIndex === -1) return null

            // Calculate the position of this specific bar within the category group
            // When stacked, all bars start at the same position (barAreaStart)
            const barStart = stacked ? barAreaStart : barAreaStart + barIndex * barWidth
            const barEnd = barStart + barWidth

            // Apply inset to stay within the bar bounds
            const inset = barWidth * insetPercent
            const markerStart = barStart + inset
            const markerEnd = barEnd - inset

            if (isVertical) {
              // For vertical layout (horizontal bars), marker is a vertical line
              return (
                <line
                  key={`marker-${markerIndex}-${dataIndex}-${barKey}`}
                  x1={markerPos}
                  y1={markerStart}
                  x2={markerPos}
                  y2={markerEnd}
                  stroke={color}
                  strokeWidth={strokeWidth}
                  strokeOpacity={0.5}
                />
              )
            } else {
              // For horizontal layout (vertical bars), marker is a horizontal line
              return (
                <line
                  key={`marker-${markerIndex}-${dataIndex}-${barKey}`}
                  x1={markerStart}
                  y1={markerPos}
                  x2={markerEnd}
                  y2={markerPos}
                  stroke={color}
                  strokeWidth={strokeWidth}
                  strokeOpacity={0.5}
                />
              )
            }
          })
        })
      })}
    </g>
  )
}

export function ChartBarContent({
  uiSchema,
  dataSchema,
  tileFilters,
  reloadHandler,
}: {
  uiSchema: ChartBarUISchema
  dataSchema: ChartBarDataSchema
  tileFilters?: React.ReactNode
  reloadHandler?: () => void
}) {
  const { title = undefined, description = undefined, chartConfig, emphasizeHeader } = uiSchema
  const {
    config: originalConfig,
    barDataKeys,
    orientation,
    categoryKey,
    stacked,
    hideAxis,
    xAxisOrientation,
    showBarLabels,
    showBarBackgrounds,
    markers,
    fixedSize = false,
    lineClampCategoryLabels,
  } = chartConfig
  const { data: rawData } = dataSchema

  if (!hasChartBarData(dataSchema)) {
    return (
      <ChartBarEmptyResponse
        title={title}
        description={description}
        emphasizeHeader={emphasizeHeader}
        tileFilters={tileFilters}
        reloadHandler={reloadHandler}
      />
    )
  }

  const { data: chartData, mapDataKey } = normalizeRechartsData(rawData, [
    categoryKey,
    ...barDataKeys,
    ...(markers?.flatMap((marker) => [marker.dataKey, ...(marker.barDataKeys ?? [])]) ?? []),
  ])
  const normalizedCategoryKey = mapDataKey(categoryKey)
  const normalizedConfig = remapRechartsRecordKeys(originalConfig, mapDataKey)

  const hasHeader = !!title && !!tileFilters
  const { contentHeight } = getComponentHeights(emphasizeHeader, hasHeader)
  const isVertical = orientation === 'vertical'

  // Fixed size calculation: space per category row for comfortable viewing
  const FIXED_SIZE_PER_CATEGORY = 60 // pixels per category item
  const CHART_PADDING = 80 // Additional padding for axes, legend, margins
  const dataRowCount = rawData.length

  // Calculate fixed dimensions based on orientation
  const fixedHeight = isVertical ? dataRowCount * FIXED_SIZE_PER_CATEGORY + CHART_PADDING : undefined
  const fixedWidth = !isVertical ? dataRowCount * FIXED_SIZE_PER_CATEGORY + CHART_PADDING : undefined

  const getEffectiveStackId = (key: string): string | undefined => {
    if (!stacked) return undefined
    return originalConfig[key as keyof typeof originalConfig].stackId ?? 'stack'
  }

  const [dataDomainMin, dataDomainMax] = computeBarChartDomain(rawData, barDataKeys, stacked, getEffectiveStackId)

  const categoryLabels = rawData.map((item) => String(item[categoryKey] || ''))
  const categoryYAxisWidth = calculateCategoryYAxisWidth(categoryLabels, lineClampCategoryLabels)
  const categoryXAxisHeight = lineClampCategoryLabels
    ? calculateVerticalTickAxisBandSize(categoryLabels, lineClampCategoryLabels)
    : calculateAngledAxisBandSize(categoryLabels, 45)

  const dataAxisTicks = generateDataAxisTicks(dataDomainMin, dataDomainMax)
  const domainMin = dataAxisTicks[0] ?? dataDomainMin
  const domainMax = dataAxisTicks[dataAxisTicks.length - 1] ?? dataDomainMax

  const getBarEndRadius = (): [number, number, number, number] => (isVertical ? [0, 8, 8, 0] : [8, 8, 0, 0])

  const getBarsInSameStack = (key: string): string[] => {
    const currentStackId = getEffectiveStackId(key)

    return barDataKeys.filter((barKey: string) => getEffectiveStackId(barKey) === currentStackId)
  }

  const getLastNonZeroBarKeyInStackRow = (row: DataItem, barsInStack: string[]): string | undefined => {
    for (let i = barsInStack.length - 1; i >= 0; i--) {
      if ((Number(row[barsInStack[i]]) || 0) !== 0) {
        return barsInStack[i]
      }
    }

    return undefined
  }

  const getStackEndRadiusForRow = (row: DataItem, key: string): number | [number, number, number, number] => {
    const lastNonZeroKey = getLastNonZeroBarKeyInStackRow(row, getBarsInSameStack(key))
    return lastNonZeroKey === key ? getBarEndRadius() : 0
  }

  const getBarRadius = (_key: string, _index: number): number | [number, number, number, number] | undefined => {
    if (!stacked) {
      return getBarEndRadius()
    }

    return 0
  }

  // When stacked, only show background on the first bar in each stack to avoid overlapping backgrounds
  const getBarBackground = (key: string): boolean => {
    if (!showBarBackgrounds) return false
    if (!stacked) return true

    // Get the effective stackId for this bar
    const currentStackId = getEffectiveStackId(key)

    // Find all bars with the same stackId
    const barsInSameStack = barDataKeys.filter((barKey: string) => {
      return getEffectiveStackId(barKey) === currentStackId
    })

    // Only show background on the first bar in the stack
    return key === barsInSameStack[0]
  }

  // Custom tick renderers for category axis labels with line clamping (native SVG text)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderClampedYAxisTick = (props: any) => {
    const { x, y, payload } = props
    const lines = wrapCategoryLabelLines(String(payload.value), CATEGORY_LABEL_WRAP_WIDTH, lineClampCategoryLabels!)

    return (
      <text x={x} y={y} fontSize={11} fill="hsl(var(--muted-foreground))">
        <title>{String(payload.value)}</title>
        {lines.map((line, i) => (
          <tspan
            key={i}
            x={x}
            dy={i === 0 ? -(lines.length - 1) * (TICK_LINE_HEIGHT / 2) : TICK_LINE_HEIGHT}
            textAnchor="end"
            dominantBaseline="central"
          >
            {line}
          </tspan>
        ))}
      </text>
    )
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderClampedXAxisTick = (props: any) => {
    const { x, y, payload } = props
    const lines = wrapCategoryLabelLines(String(payload.value), CATEGORY_LABEL_WRAP_WIDTH, lineClampCategoryLabels!)

    return (
      <text
        x={x}
        y={y}
        fontSize={11}
        fill="hsl(var(--muted-foreground))"
        transform={`rotate(-90, ${x}, ${y})`}
        textAnchor="end"
      >
        <title>{String(payload.value)}</title>
        {lines.map((line, i) => (
          <tspan key={i} x={x} dy={i === 0 ? 0 : TICK_LINE_HEIGHT} dominantBaseline="central">
            {line}
          </tspan>
        ))}
      </text>
    )
  }

  const verticalLayout = (
    <>
      <YAxis
        dataKey={normalizedCategoryKey}
        type={'category'}
        tickLine={false}
        tickMargin={10}
        axisLine={false}
        width={categoryYAxisWidth}
        interval={0}
        {...(lineClampCategoryLabels ? { tick: renderClampedYAxisTick } : {})}
      />
      <XAxis
        type="number"
        hide={hideAxis}
        domain={[domainMin, domainMax]}
        ticks={dataAxisTicks}
        orientation={xAxisOrientation}
        tickFormatter={(value: number) => value.toLocaleString()}
      />
    </>
  )

  const horizontalLayout = (
    <>
      <XAxis
        dataKey={normalizedCategoryKey}
        tickLine={false}
        tickMargin={10}
        axisLine={false}
        interval={0}
        height={categoryXAxisHeight}
        {...(lineClampCategoryLabels
          ? {
              tick: renderClampedXAxisTick,
            }
          : {
              angle: -45,
              textAnchor: 'end' as const,
            })}
      />
      <YAxis
        hide={hideAxis}
        domain={[domainMin, domainMax]}
        ticks={dataAxisTicks}
        tickFormatter={(value: number) => value.toLocaleString()}
      />
    </>
  )

  return (
    <div className="flex h-full flex-col">
      {hasHeader && (
        <ComponentHeader emphasize={emphasizeHeader}>
          <ComponentTitle title={title} description={description} />
          <ComponentActionsContainer>
            {CAN_REFRESH && reloadHandler && (
              <TooltipButton tooltip="Refresh data">
                <Button size="icon" variant="ghost" className="size-8 rounded-full" onClick={reloadHandler}>
                  <IconRefreshOutline className="size-4" />
                </Button>
              </TooltipButton>
            )}
            {tileFilters}
          </ComponentActionsContainer>
        </ComponentHeader>
      )}
      <div
        className={clsx({
          'pt-5': hasHeader,
          'overflow-y-auto': fixedSize && isVertical,
          'overflow-x-auto': fixedSize && !isVertical,
        })}
        style={{ height: contentHeight }}
      >
        <ChartContainer
          className={clsx('mx-auto aspect-auto min-h-0 [&_.recharts-wrapper]:overflow-visible', {
            'h-full w-full': !fixedSize,
            'w-full': fixedSize && isVertical,
            'h-full': fixedSize && !isVertical,
          })}
          config={normalizedConfig}
          legend={<ChartLegendContent items={getChartLegendItems(originalConfig, barDataKeys)} className="pt-1" />}
          style={{
            ...(fixedSize && isVertical && fixedHeight ? { height: fixedHeight, minHeight: fixedHeight } : {}),
            ...(fixedSize && !isVertical && fixedWidth ? { width: fixedWidth, minWidth: fixedWidth } : {}),
          }}
        >
          <BarChart accessibilityLayer data={chartData} layout={orientation} barGap={0}>
            <CartesianGrid vertical={isVertical} horizontal={!isVertical} />
            {isVertical ? verticalLayout : horizontalLayout}
            <ChartTooltip cursor={false} content={<ChartTooltipContent />} />
            {barDataKeys.map((key: string, index: number) => (
              <Bar
                dataKey={mapDataKey(key)}
                fill={originalConfig[key as keyof typeof originalConfig].color}
                radius={getBarRadius(key, index)}
                key={key}
                className="w-full"
                stackId={getEffectiveStackId(key)}
                background={getBarBackground(key)}
                label={
                  showBarLabels
                    ? {
                        position: isVertical ? 'insideRight' : 'insideTop',
                        ...(stacked
                          ? {
                              valueAccessor: (entry: { value?: number | [number, number]; payload?: DataItem }) =>
                                getStackedBarSegmentValue(entry, mapDataKey(key)),
                            }
                          : {}),
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        content: (props: any) => {
                          const rowIndex = props.index
                          const segmentValue = stacked
                            ? rowIndex != null
                              ? Number(rawData[rowIndex]?.[key]) || 0
                              : getStackedBarSegmentValue(props, mapDataKey(key))
                            : Number(props.value) || 0

                          return renderBarLabel(props, {
                            isVertical,
                            segmentValue,
                            fill: getContrastTextColor(originalConfig[key as keyof typeof originalConfig].color),
                          })
                        },
                      }
                    : undefined
                }
              >
                {stacked &&
                  rawData.map((row, rowIndex) => (
                    <Cell
                      key={`${key}-${rowIndex}`}
                      {...({ radius: getStackEndRadiusForRow(row, key) } as React.SVGProps<SVGElement>)}
                    />
                  ))}
              </Bar>
            ))}
            {markers && markers.length > 0 && (
              <Customized
                component={(props: {
                  xAxisMap?: BarMarkersProps['xAxisMap']
                  yAxisMap?: BarMarkersProps['yAxisMap']
                }) => (
                  <BarMarkers
                    xAxisMap={props.xAxisMap}
                    yAxisMap={props.yAxisMap}
                    data={rawData}
                    markers={markers}
                    categoryKey={categoryKey}
                    allBarDataKeys={barDataKeys}
                    isVertical={isVertical}
                    stacked={stacked}
                  />
                )}
              />
            )}
          </BarChart>
        </ChartContainer>
      </div>
    </div>
  )
}
